
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { Shield, Server, Activity, Lock, AlertTriangle, AlertOctagon } from 'lucide-react';
import RbacVisualizer from './RbacVisualizer';
import LogConfigurationTable from '../../components/LogConfigurationTable';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

const AutoGainSecurityPortal: React.FC = () => {
    useAppContext(); // Context accessed for side effects but currentUser not used
    const navigate = useNavigate();
    const [lockdownActive, setLockdownActive] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordInput, setPasswordInput] = useState("");
    const [authError, setAuthError] = useState<string | null>(null);

    useEffect(() => {
        fetchSystemStatus();
    }, []);

    const fetchSystemStatus = async () => {
        try {
            const data = await authenticatedFetchJSON<{ lockdown_active: boolean }>('/api/admin/security/system-status');
            setLockdownActive(data.lockdown_active);
        } catch (error) {
            console.error("Failed to fetch system status", error);
        }
    };

    const handleLockdownClick = () => {
        setShowPasswordModal(true);
    };

    const confirmLockdownAction = async () => {
        try {
            // Verify Password First
            await authenticatedFetchJSON('/api/admin/security/verify-password', {
                method: 'POST',
                body: JSON.stringify({ password: passwordInput })
            });

            // Toggle Lockdown
            const newStatus = !lockdownActive;
            await authenticatedFetchJSON('/api/admin/security/emergency-lockdown', {
                method: 'POST',
                body: JSON.stringify({ enable: newStatus })
            });

            setLockdownActive(newStatus);
            setShowPasswordModal(false);
            setPasswordInput("");
            setAuthError(null);
        } catch {
            setAuthError("Authentication Failed. Access Denied.");
        }
    };

    return (
        <div className={`space-y-8 p-6 animate-in fade-in duration-500 min-h-screen transition-colors ${lockdownActive ? 'bg-red-50' : ''}`}>
            {/* Header */}
            <div className={`bg-slate-900 -m-6 mb-2 p-8 text-white relative overflow-hidden transition-colors ${lockdownActive ? 'bg-red-900' : ''}`}>
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
                <div className="relative z-10 flex justify-between items-center">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className={`${lockdownActive ? 'bg-red-500' : 'bg-blue-600'} p-2 rounded-lg transition-colors`}>
                                <Shield size={24} className="text-white" />
                            </div>
                            <span className={`${lockdownActive ? 'text-red-200' : 'text-blue-400'} font-bold tracking-wider text-sm uppercase`}>Platform Administration</span>
                        </div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            AutoGain Security Command
                            {lockdownActive && <span className="px-3 py-1 bg-red-600/50 border border-red-500 rounded-lg text-sm font-bold animate-pulse">LOCKDOWN ACTIVE</span>}
                        </h1>
                        <p className="text-slate-400 mt-2 max-w-2xl">
                            Master control for Role-Based Access Control (RBAC), Client Organization isolation, and global security policies.
                        </p>
                    </div>
                </div>
            </div>

            {/* Main RBAC Visualizer */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3 space-y-6">
                    <div className="h-[700px] bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden relative">
                        <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded border border-slate-200 text-xs font-bold text-slate-500">
                            System Access Architecture
                        </div>
                        <RbacVisualizer />
                        {lockdownActive && (
                            <div className="absolute inset-0 bg-red-500/10 backdrop-blur-[1px] flex items-center justify-center z-20">
                                <div className="bg-white p-6 rounded-2xl shadow-2xl border-2 border-red-500 text-center max-w-md">
                                    <AlertOctagon size={48} className="text-red-500 mx-auto mb-4" />
                                    <h3 className="text-xl font-bold text-slate-800 mb-2">System Restricted</h3>
                                    <p className="text-slate-600">
                                        Emergency protocols enable. Non-critical access has been suspended.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                            <h3 className="font-bold text-slate-800">Global Logging & Subscription Limits</h3>
                        </div>
                        <div className="h-[400px]">
                            <LogConfigurationTable />
                        </div>
                    </div>
                </div>

                {/* Control Panel */}
                <div className="space-y-6">
                    {/* Platform Actions */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Platform Controls</h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => navigate('/platform/clients')}
                                disabled={lockdownActive}
                                className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-blue-50 hover:text-blue-700 text-slate-700 font-medium transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Server size={18} /> Clients & Tenants
                            </button>
                            <button
                                onClick={() => navigate('/platform/analytics')}
                                disabled={lockdownActive}
                                className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-blue-50 hover:text-blue-700 text-slate-700 font-medium transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Activity size={18} /> Global Analytics
                            </button>
                            <button
                                onClick={() => navigate('/platform/debug')}
                                disabled={lockdownActive}
                                className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-blue-50 hover:text-blue-700 text-slate-700 font-medium transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Activity size={18} /> System Debug
                            </button>
                        </div>
                    </div>

                    {/* Security Stats */}
                    <div className={`rounded-2xl p-6 text-white shadow-xl transition-colors ${lockdownActive ? 'bg-red-950 border border-red-500' : 'bg-slate-900'}`}>
                        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Security Status</h3>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                                <span className="text-slate-300">RBAC Enforcement</span>
                                <span className={`px-2 py-0.5 text-xs rounded font-bold ${lockdownActive ? 'bg-red-500 text-white' : 'bg-emerald-500/20 text-emerald-400'}`}>
                                    {lockdownActive ? 'STRICT' : 'ACTIVE'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                                <span className="text-slate-300">Multi-Tenancy</span>
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded font-bold">ISOLATED</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-slate-300">Admin Sessions</span>
                                <span className="text-white font-mono">1</span>
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-slate-800">
                            <button
                                onClick={handleLockdownClick}
                                className={`w-full py-3 rounded-lg text-sm font-bold transition-all shadow-lg flex items-center justify-center gap-2 ${lockdownActive
                                    ? 'bg-white text-red-900 hover:bg-slate-100'
                                    : 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                                    }`}
                            >
                                {lockdownActive ? (
                                    <>
                                        <Shield size={16} /> END LOCKDOWN
                                    </>
                                ) : (
                                    <>
                                        <AlertTriangle size={16} /> EMERGENCY LOCKDOWN
                                    </>
                                )}
                            </button>
                            <p className="text-xs text-center mt-2 text-slate-500">
                                {lockdownActive
                                    ? 'System is currently frozen. Authenticate to lift.'
                                    : 'Press to freeze all non-admin sessions immediately.'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Password Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in duration-200">
                        <div className={`p-6 text-white text-center ${lockdownActive ? 'bg-emerald-600' : 'bg-red-600'}`}>
                            <Lock size={32} className="mx-auto mb-2 opacity-90" />
                            <h3 className="text-lg font-bold">
                                {lockdownActive ? 'Authenticate to Restore' : 'Security Verification'}
                            </h3>
                            <p className="text-xs opacity-80 mt-1">
                                {lockdownActive ? 'Lift the system lockdown' : 'Authorize Emergency Lockdown'}
                            </p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Admin Password</label>
                                <input
                                    type="password"
                                    value={passwordInput}
                                    onChange={(e) => setPasswordInput(e.target.value)}
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-lg"
                                    autoFocus
                                    placeholder="••••••••"
                                />
                            </div>
                            {authError && (
                                <div className="text-xs text-red-500 font-bold bg-red-50 p-2 rounded flex items-center gap-2">
                                    <AlertOctagon size={12} /> {authError}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setPasswordInput("");
                                        setAuthError(null);
                                    }}
                                    className="px-4 py-2 text-slate-600 font-bold hover:bg-slate-50 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmLockdownAction}
                                    className={`px-4 py-2 text-white font-bold rounded-lg shadow-sm ${lockdownActive ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                >
                                    {lockdownActive ? 'Lift Lockdown' : 'CONFIRM'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoGainSecurityPortal;
